import { Box, Typography, useTheme, Card, CardContent, Button, LinearProgress, Grid, Chip, Divider, CircularProgress, Alert } from "@mui/material";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HeaderWithControls from "../../components/HeaderWithControls";
import HelpModal from "../../components/HelpModal";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { tokens } from "../../theme";
import { useState, useEffect, useRef, useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement } from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { API_BASE, logEnvironmentConfig } from '../../config/environment';
import { apiGet, apiPost } from '../../utils/api';
import { buildConfigDataFromActivity } from '../../utils/restoreRunConfig';
import { useTranslation } from "react-i18next";

// Import the new components
import TrainingStatusCard from './components/TrainingStatusCard';
import TrainingControlPanel from './components/TrainingControlPanel';
import LaunchStatusBanner from './components/LaunchStatusBanner';
import PreRunChecksPanel from './components/PreRunChecksPanel';
import SchemaValidationDetailsCard from './components/SchemaValidationDetailsCard';
import { computeSelectionSignature } from '../../utils/mockSchemaValidation';
import { runSchemaValidation } from '../../utils/schemaValidation';
import {
  playMockTraining,
  selectLogTemplate,
} from '../../utils/mockTrainingPlayback';
import PipelineResultCardViewer from './components/PipelineResultCardViewer';
import { resolveMockResultCard } from '../../data/mockPipelineResultCards';
import SegmentationResults from './components/SegmentationResults';
import IntegratedTrainingResults from './components/IntegratedTrainingResults';
import ExecutionLogsCard from './components/ExecutionLogsCard';
import TrainingAnimation from './components/TrainingAnimation';
import AllCompletedRunsModal from '../../modals/AllCompletedRunsModal';
import { resolveDatasetDisplayNameForExecution } from '../../utils/datasetDisplay';
import { formatDateToLocal, getCurrentUTCDate } from '../../utils/dateUtils';

const Execute = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const [helpOpen, setHelpOpen] = useState(false);
  
  // Add fallback colors in case tokens function fails
  const fallbackColors = {
    grey: { 100: 'var(--fx-paper)', 300: '#cccccc', 400: '#999999', 600: '#666666', 700: '#333333', 800: '#1a1a1a' },
    primary: { 400: '#1976d2', 500: '#115293' },
    greenAccent: { 400: '#4caf50', 500: '#388e3c', 600: '#2e7d32', 700: '#1b5e20' },
    blueAccent: { 300: '#64b5f6', 600: '#1e88e5', 700: '#1565c0' },
    redAccent: { 300: '#e57373', 600: '#e53935', 700: '#c62828' },
    secondary: { 100: '#e3f2fd', 300: '#90caf9', 500: '#2196f3', 600: '#1976d2' }
  };
  
  const colors = tokens(theme.palette.mode) || fallbackColors;

  // Use shared context instead of local state for config, status, and progress
  const { 
    config: currentConfig,
    featureMetadata,
    fetchDisplayableFeatures, 
    trainingStatus, 
    trainingProgress,
    executionHistory,
    isCompleted,
    setIsCompleted,
    isRunning,
    setIsRunning,
    isPaused,
    setIsPaused,
    selectedFeatures,
    featureVisualizations,
    setFeatureVisualizations,
    configLoading,
    executeTraining,
    pauseTraining,
    stopTraining,
    clearLogs,
    generateFeatureVisualizations,
    isTrainingCompleted,
    refreshTrainingStatus,
    refreshTrainingProgress,
    fetchTrainingProgress,
    clearCachedData,
    clearAllTrainingData,
    fetchConfig,
    saveConfig, // dev-0.7.51 — used by "Use This Configuration" restore
    isClearing,
    // Phase F1.x prototype — schema validation gate state.
    selectedSites,
    siteCapabilities,
    sites,
    schemaPreCheck,
    setSchemaPreCheck,
    // Used by TrainingStatusCard to resolve legacy capability_id tokens
    // (configs.selected_datasets) into logical dataset display names.
    availableDatasets,
    // Segmentation states and functions
    segmentationStatus,
    setSegmentationStatus,
    segmentationProgress,
    segmentationVisualizations,
    currentSegmentationRun,  // Fixed: context exports this as currentSegmentationRun, not segmentationCurrentRun
    segmentationHistory,
    segmentationLoading,
    executeSegmentation,
    fetchSegmentationStatus,
    fetchCurrentSegmentationRun,
    fetchSegmentationVisualizations,
    streamSegmentationEvents,
    fetchTrainingSummary,
    fetchFlowerTaskStatus,
    fetchFlowerTaskLog,
    // Execute page specific states (now from AppContext)
    progress,
    setProgress,
    currentRound,
    setCurrentRound,
    totalRounds,
    setTotalRounds,
    connectedClients,
    setConnectedClients,
    visualizationsGenerated,
    setVisualizationsGenerated,
    hasBeenCleared,
    setHasBeenCleared,
    isPolling,
    setIsPolling,
    error,
    setError,
    isGeneratingVisualizations,
    setIsGeneratingVisualizations,
    logs,
    setLogs,
    trainingResults,
    setTrainingResults,
    setSelectedFeatures, // Added for feature restoration
    perDatasetFeaturesMap, // Added for mapped feature coloration
    setCurrentConfig, // Needed by the Clear Configuration action
    setConfig, // The Launchpad reads `config` (aliased as `currentConfig` locally) so we must clear that one too
  } = useAppContext();

  // Phase F1.x prototype: drives the "Running…" status on PreRunChecksPanel
  // while the mock final validation gate is in flight.
  const [finalChecking, setFinalChecking] = useState(false);

  // Mock log playback handle. We keep it in a ref so Start can cancel a
  // previous run, and so component unmount can clear the in-flight timer
  // without holding a stale closure. NO real backend stream / WebSocket / SSE.
  // Source: 2026-05-25_training_progress_mock_server_side_only_v2.md.
  const mockPlaybackRef = useRef(null);

  // Cancel any in-flight mock playback on unmount. This is the only timer
  // owned by this surface; nothing else needs cleanup here.
  useEffect(() => {
    return () => {
      if (mockPlaybackRef.current) {
        mockPlaybackRef.current.cancel();
        mockPlaybackRef.current = null;
      }
    };
  }, []);

  // Clear visualizations and results on component mount (page refresh)
  useEffect(() => {
    // Only clear local state, don't clear cached config data
    // setFeatureVisualizations(null); // Keep visualizations when navigating back
    // setIsCompleted(false); // Keep completion state when navigating back
    // Don't clear backend cache on mount as this removes the configuration
    // clearCachedData(); // Removed this line to preserve config
    
    // Log environment configuration for debugging
    logEnvironmentConfig();
    
    // Fetch latest training progress and status when component mounts
    refreshTrainingStatus();
    refreshTrainingProgress();

    // Load persisted training results and selected features
    try {
        const savedResults = localStorage.getItem('trainingResults');
        if (savedResults && !trainingResults) {
            const parsedResults = JSON.parse(savedResults);
            setTrainingResults(parsedResults);
            
            const savedCompleted = localStorage.getItem('trainingStatusCompleted') === 'true';
            
            // Only set isCompleted if explicitly saved as true
            if (savedCompleted && Object.keys(parsedResults).length > 0) {
                setIsCompleted(true);
            }
        }
        
        // Restore selected features if available
        const savedFeatures = localStorage.getItem('selectedFeatures');
        if (savedFeatures && (!selectedFeatures || selectedFeatures.length === 0)) {
            try {
                const features = JSON.parse(savedFeatures);
                if (Array.isArray(features) && features.length > 0) {
                    setSelectedFeatures(features);
                }
            } catch (e) {
                console.error("Failed to parse saved selected features", e);
            }
        } 
        
        // Restore feature visualizations if available
        const savedViz = localStorage.getItem('featureVisualizations');
        if (savedViz && (!featureVisualizations || Object.keys(featureVisualizations).length === 0)) {
            try {
                const viz = JSON.parse(savedViz);
                setFeatureVisualizations(viz);
                setVisualizationsGenerated(true); // preventing re-generation logic if already loaded
            } catch (e) {
                console.error("Failed to parse saved feature visualizations", e);
            }
        }
    } catch (e) {
        console.error("Failed to load persisted execution data", e);
    }
  }, []);
  
  // Fetch segmentation current run when component mounts if segmentation is completed
  useEffect(() => {
    if (segmentationStatus === 'completed' && currentConfig?.dataset_config?.processing_type === 'imaging_segmentation') {

      fetchCurrentSegmentationRun()
        .then(runState => {

        })
        .catch(error => {
          console.error('[EXECUTE] Failed to fetch current segmentation run on mount:', error);
        });
    }
  }, [segmentationStatus, currentConfig]);
  
  // Also fetch current run whenever visualizations are available
  useEffect(() => {
    if (segmentationVisualizations && segmentationVisualizations.length > 0 && !currentSegmentationRun) {

      fetchCurrentSegmentationRun()
        .then(runState => {

        })
        .catch(error => {
          console.error('[EXECUTE] Failed to fetch current segmentation run for visualizations:', error);
        });
    }
  }, [segmentationVisualizations, currentSegmentationRun]);
  
  // Refresh configuration when component mounts only if we don't already have one
  useEffect(() => {
    if (!currentConfig) {
      // Fetch the latest configuration to ensure Execute page shows updated config
      fetchConfig();
    }
  }, [currentConfig]);

  // Ensure feature metadata is available for correct rendering of filtered text.
  // Guard against re-fetching the same dataset path: the fetch sets state
  // (incl. featureMetadata) on both success and failure, so depending on
  // featureMetadata/fetchDisplayableFeatures here caused an infinite
  // render -> fetch -> setState -> render loop whenever the dataset 500'd.
  const attemptedFeaturePathRef = useRef(null);
  useEffect(() => {
    const datasetPath = currentConfig?.dataset_config?.dataset_path;
    if (datasetPath &&
        currentConfig.dataset_config.processing_type === 'tabular' &&
        (!featureMetadata || Object.keys(featureMetadata).length === 0) &&
        attemptedFeaturePathRef.current !== datasetPath) {

        attemptedFeaturePathRef.current = datasetPath;
        fetchDisplayableFeatures(datasetPath);
    }
  }, [currentConfig?.dataset_config?.dataset_path, currentConfig?.dataset_config?.processing_type]);
  // All Executions Modal Logic
  const [showAllCompletedRuns, setShowAllCompletedRuns] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const getModelName = (modelType) => {
    switch(modelType) {
      case 'logreg': return 'Logistic Regression';
      case 'mlp': return 'Multi-Layer Perceptron';
      case 'tabnet': return 'TabNet';
      case 'ft_transformer': return 'FT-Transformer';
      case 'cnn': return 'Convolutional Neural Network';
      case 'lstm': return 'LSTM';
      case 'transformer': return 'Transformer';
      case 'convnext': return 'ConvNeXt';
      case 'resnet18': return 'ResNet18';
      case 'swintransformer': return 'Swin Transformer';
      default: return modelType || 'Unknown';
    }
  };

  const getDatasetName = (datasetPath) => {
    if (!datasetPath) return 'Unknown';
    return datasetPath.split('/').pop() || datasetPath;
  };

  const getAllCompletedRuns = useMemo(() => {
    const allRuns = [...executionHistory];
    
    const mappedHistory = allRuns.map((execution, index) => {
        const resolved = resolveDatasetDisplayNameForExecution(execution, {
            siteCapabilities,
            availableDatasets,
        });
        return {
            id: execution.id || `execution-${index}`,
            type: "execution",
            title: 'Training Run',
            description: `Model: ${getModelName(execution.modelType)}, Dataset: ${resolved}`,
            time: formatDateToLocal(execution.timestamp),
            status: execution.status || (execution.completed ? 'completed' : 'incomplete'),
            executionData: execution
        };
    });

    if (trainingStatus && (trainingStatus.status === 'running' || trainingStatus.status === 'paused')) {
        const activeRunId = trainingStatus.trainingRunId || 'current-active-run';
        const exists = mappedHistory.some(run => run.id === activeRunId || run.executionData.trainingRunId === activeRunId);
        
        if (!exists) {
            mappedHistory.unshift({
                id: activeRunId,
                title: `Training Run ${activeRunId}`,
                description: `Model: ${currentConfig?.dataset_config?.model_type || 'Unknown'} | Dataset: ${getDatasetName(currentConfig?.dataset_config?.dataset_path)}`,
                time: "In Progress",
                status: trainingStatus.status,
                type: 'execution',
                executionData: {
                    modelType: currentConfig?.dataset_config?.model_type || 'Unknown',
                    datasetPath: currentConfig?.dataset_config?.dataset_path || 'Unknown',
                    rounds: currentConfig?.app_config?.["num-server-rounds"] || 0,
                    completed: false,
                    currentRound: trainingProgress?.progress?.current_round || 0,
                    accuracy: trainingProgress?.latest_metrics?.round_metrics?.accuracy || 0,
                    loss: trainingProgress?.latest_metrics?.round_metrics?.loss || 0,
                    trainingRunId: activeRunId
                }
            });
        }
    }
    
    return mappedHistory;
  }, [executionHistory, trainingStatus, currentConfig, trainingProgress, siteCapabilities, availableDatasets]);

  // dev-0.7.51 — "Use This Configuration" from the Execution Details modal.
  // Was previously a no-op stub, so the button did nothing when the modal was
  // opened from the Execute page. Now mirrors the Dashboard handler exactly via
  // the shared restore helper: rebuild the full config (all datapoints) from
  // the past run, persist it, reset stale training state, and navigate to the
  // configs page where the form/site-picker hydrate from the restored config.
  const handleActivityClick = async (activity) => {
    try {
      const configData = await buildConfigDataFromActivity(activity);
      if (!configData) {
        console.warn('[Execute] Could not build config from activity', activity);
        return;
      }
      console.log('[Execute] Restored configData:', configData);

      if (typeof saveConfig === 'function') {
        await saveConfig(configData);
      } else {
        // Fallback: at least populate context so the configs page hydrates.
        if (typeof setConfig === 'function') setConfig(configData);
        if (typeof setCurrentConfig === 'function') setCurrentConfig(configData);
      }

      if (typeof clearAllTrainingData === 'function') {
        await clearAllTrainingData();
      }
      if (typeof setCurrentConfig === 'function') {
        setCurrentConfig(configData);
      }

      // restored:true tells the Configs page to honour this past run's
      // hyperparameters instead of overriding them with the Settings defaults.
      navigate('/configs', { state: { restored: true } });
    } catch (error) {
      console.error('[Execute] Error loading configuration:', error);
    }
  };


  // Initialize component - fetch current status from backend
  useEffect(() => {

    
    // DON'T reset isRunning/isPaused/isCompleted here!
    // These states are managed by AppContext based on backend status
    // Resetting them here causes race conditions
    
    // Reset segmentation state
    setSegmentationStatus(null);
    
    // Clear any existing polling intervals
    if (segmentationPollingRef.current) {
      clearInterval(segmentationPollingRef.current);
      segmentationPollingRef.current = null;
    }
    
    // Fetch actual status from backend - AppContext will sync isRunning based on response
    refreshTrainingStatus();
    refreshTrainingProgress();
  }, []); // Empty dependency array means this runs once on mount

  // Ref for interval polling (not state, so kept local)
  const intervalRef = useRef(null);

  // Dynamic API base URL from environment configuration

  // Initialize local UI state when component mounts
  useEffect(() => {

    
    // DON'T reset progress/round/clients to 0 on mount!
    // This causes progress bar to disappear when navigating back during training
    // Instead, let the trainingProgress useEffect sync them from backend
    
    // Only reset trainingResults if not already set
    // setTrainingResults(null);
    
    // Fetch initial status - AppContext will sync isRunning/isPaused/isCompleted
    // AND the trainingProgress useEffect will sync progress/round/clients
    refreshTrainingStatus();
    refreshTrainingProgress();
  }, []); // Empty dependency array means this runs once on mount

  // Update local state when context data changes (optimized to prevent flickering)
  useEffect(() => {
    if (trainingProgress) {

      
      // CRITICAL: Don't restore progress if training is not actually running
      // This prevents stale data from showing after stop/clear
      const isActuallyRunning = trainingProgress.status === 'running' || 
                                trainingStatus?.status === 'running' ||
                                isRunning;
      
      if (!isActuallyRunning && trainingProgress.status !== 'completed') {

        return;
      }
      
      // Batch state updates to prevent multiple re-renders
      const newProgress = trainingProgress.progress?.percentage || 0;
      const newCurrentRound = trainingProgress.progress?.current_round || 0;
      const newConnectedClients = trainingProgress.total_clients || 0;
      const configuredRounds = currentConfig?.app_config?.["num-server-rounds"] || 10;
      
      // Only update if values actually changed to prevent unnecessary re-renders
      const progressChanged = progress !== newProgress;
      const roundChanged = currentRound !== newCurrentRound;
      
      // Trust backend `progress.percentage` as the source of truth. The
      // previous "monotonic guard" (only accept newProgress > prev) was
      // imported from segmentation polling to defend against race
      // conditions, but for Flower training polling it caused intermediate
      // round percentages (e.g. 33%, 66%) to stay stuck at 0% under some
      // transition timing — backend reports `current_round=1,
      // percentage=33.33` correctly per Phase 2.x dev-0.7.6 smoke, so we
      // just follow it. Comparison still avoids unnecessary re-renders
      // when the value did not change.
      setProgress(prev => (prev !== newProgress ? newProgress : prev));
      setCurrentRound(prev => prev !== newCurrentRound ? newCurrentRound : prev);
      setConnectedClients(prev => prev !== newConnectedClients ? newConnectedClients : prev);
      setTotalRounds(prev => prev !== configuredRounds ? configuredRounds : prev);
      
      // Add new logs if available - prioritize latest_metrics, fallback to progress changes
      if (trainingProgress.latest_metrics?.round_metrics) {
        const roundMetrics = trainingProgress.latest_metrics.round_metrics;
        const metricsRound = roundMetrics.round;
        if (!metricsRound || lastMetricsRoundLoggedRef.current !== metricsRound) {
          addLog(
            `Round ${roundMetrics.round}: Loss=${roundMetrics.loss?.toFixed(4)}, Accuracy=${roundMetrics.accuracy?.toFixed(4)}`,
            'info'
          );
          if (metricsRound) {
            lastMetricsRoundLoggedRef.current = metricsRound;
          }
        }
      } else if (roundChanged && newCurrentRound > 0) {
        // Log round progress even without detailed metrics
        addLog(`Round ${newCurrentRound}/${configuredRounds} - Progress: ${Math.round(newProgress)}%`, 'info');
      } else if (progressChanged && newProgress > 0 && trainingProgress.status === 'running') {
        // Log general progress updates during training
        addLog(`Training progress: ${Math.round(newProgress)}%`, 'info');
      }
      
      // Process detailed logs from backend
      if (trainingProgress.detailed_logs && Array.isArray(trainingProgress.detailed_logs)) {
        trainingProgress.detailed_logs.forEach(detailedLog => {
          addLog(
            detailedLog.message, 
            detailedLog.level || 'debug', 
            detailedLog.detailed !== undefined ? detailedLog.detailed : true
          );
        });
      }
      
      // Only mark as completed if status is explicitly 'completed' AND we have results
      if (trainingProgress.status === 'completed' && trainingProgress.progress?.percentage >= 100) {

        setIsCompleted(true);
        setIsRunning(false);

        // Log final Flower CLI progress and configuration from the Flower task
        const flowerTask = trainingStatus?.flower_task;
        if (flowerTask && !flowerCompletionLogsLoggedRef.current) {
          const p = flowerTask.progress || {};
          const o = flowerTask.overrides || {};
          const pct = typeof p.percentage === 'number' ? Math.round(p.percentage) : 0;
          addLog(
            `Final Flower progress: round ${p.current_round || 0}/${p.total_rounds || 0}, ${pct}%`,
            'info'
          );
          addLog(
            `Flower training config: model=${o.model_type || 'n/a'}, rounds=${o['num-server-rounds'] || 'n/a'}, lr=${o['learning-rate'] || 'n/a'}, batch=${o['batch-size'] || 'n/a'}, clients=${o.num_clients || 'n/a'}`,
            'info'
          );
          flowerCompletionLogsLoggedRef.current = true;
        }
        
        // Refresh training summary to update dashboard with new database results

        if (fetchTrainingSummary) {
          fetchTrainingSummary().then(() => {

          }).catch(error => {
            console.error('[EXECUTE] Failed to refresh training summary:', error);
          });
        }
        
        // Prefer real results: first try Flower CLI round_metrics from the
        // Flower task, then any all_rounds from legacy trainingProgress, and
        // finally the metrics endpoint as last resort.
        const flowerRoundMetrics = trainingStatus?.flower_task?.round_metrics;
        if (flowerRoundMetrics && Object.keys(flowerRoundMetrics).length > 0) {

          setTrainingResults(flowerRoundMetrics);
        } else if (trainingResults && Object.keys(trainingResults).length > 0) {

        } else if (trainingProgress.all_rounds && Object.keys(trainingProgress.all_rounds).length > 0) {

          setTrainingResults(trainingProgress.all_rounds);
        } else {

          // Fetch training results from metrics endpoint as fallback (no mocks)
          apiGet('/metrics/rounds')
            .then(response => {

              if (response && response.rounds && Object.keys(response.rounds).length > 0) {
                setTrainingResults(response.rounds);

              } else {

              }
            })
            .catch(error => {
              console.error('[EXECUTE] Failed to fetch training results:', error);
            });
        }
        // Only log training completion once per training session and only on transition to completed
        const alreadyLogged = localStorage.getItem('executeCompletionLogged') === 'true';
        const nowCompleted = (trainingProgress?.status === 'completed' || trainingStatus?.status === 'completed')
          && (trainingProgress?.progress?.percentage >= 100);
        if (nowCompleted && !trainingCompletedLoggedRef.current && !alreadyLogged && prevStatusRef.current !== 'completed') {
          addLog('Training completed successfully!', 'success');
          trainingCompletedLoggedRef.current = true;
          try {
            localStorage.setItem('executeCompletionLogged', 'true');
          } catch (_) {}
        }
      }
    }
  }, [trainingProgress, currentConfig]);

  // Save logs to database when training completes
  // Save logs to database when training completes (reusing flower task endpoint)
  const logsSavedRef = useRef(false);
  
  useEffect(() => {
    if (isCompleted && logs.length > 0 && !logsSavedRef.current) {
        // Use Flower Task ID to save logs via the unified endpoint
        const flowerTaskId = trainingStatus?.flower_task?.task_id;
        const rnd = currentRound || trainingProgress?.progress?.current_round || 0;
        
        if (flowerTaskId) {
            logsSavedRef.current = true;
            console.log('[EXECUTE] Saving execution logs for task:', flowerTaskId);
            // Reusing the flower_tasks endpoint (POST mode) to update logs
            apiPost(`/flower_tasks/${flowerTaskId}`, {
                execution_logs: logs,
                round: rnd
            }).then(() => {
                console.log('[EXECUTE] Logs saved successfully via task endpoint');
            }).catch(err => {
                console.error('[EXECUTE] Failed to save logs:', err);
                logsSavedRef.current = false; // Allow retry on failure
            });
        } else {
             // If no flower task ID (e.g. simulation mode without flower CLI tracking), 
             // we might not be able to save via this endpoint.
             console.log('[EXECUTE] No Flower Task ID found, skipping manual log save (backend might have handled it if simulation).');
        }
    }
    
    // Reset saved flag when training starts again
    if (!isCompleted && isRunning) {
        logsSavedRef.current = false;
    }
  }, [isCompleted, logs, trainingProgress, trainingStatus, currentRound, isRunning]);

  // Persist training results whenever they change
  useEffect(() => {
    if (trainingResults) {
        try {
            localStorage.setItem('trainingResults', JSON.stringify(trainingResults));
        } catch (e) {
            console.error("Failed to save training results", e);
        }
    }
  }, [trainingResults]);

  // Persist selected features whenever they change
  useEffect(() => {
    if (selectedFeatures && selectedFeatures.length > 0) {
        try {
            localStorage.setItem('selectedFeatures', JSON.stringify(selectedFeatures));
        } catch (e) {
            console.error("Failed to save selected features", e);
        }
    }
  }, [selectedFeatures]);

  // Persist feature visualizations whenever they change
  useEffect(() => {
    if (featureVisualizations) {
        try {
            localStorage.setItem('featureVisualizations', JSON.stringify(featureVisualizations));
        } catch (e) {
            console.error("Failed to save feature visualizations", e);
        }
    }
  }, [featureVisualizations]);

  // Persist completion status whenever it changes
  useEffect(() => {
    try {
        localStorage.setItem('trainingStatusCompleted', isCompleted);
    } catch (e) {
        console.error("Failed to save training status completed", e);
    }
  }, [isCompleted]);

  useEffect(() => {
    if (trainingStatus) {

      
      // REMOVED: Duplicate state synchronization logic
      // AppContext.fetchTrainingStatus already syncs isRunning/isPaused/isCompleted
      // Having duplicate logic here causes conflicts and race conditions
      
      // Just track previous status for logging purposes
      prevStatusRef.current = trainingStatus.status;
    }
  }, [trainingStatus, isCompleted]);
  
  // DISABLED: useEffect-based logging for segmentation
  // The startSegmentationPolling function handles all log updates with proper formatting
  // Using both creates duplicate/inconsistent log formats
  const lastLoggedProgressRef = useRef(0);
  // useEffect(() => {
  //   if (!trainingStatus || !currentConfig) return;
  //   
  //   const isSegmentation = currentConfig?.dataset_config?.processing_type === "imaging_segmentation";
  //   if (!isSegmentation) return;
  //   
  //   // Check if this is segmentation training with progress info
  //   if (trainingStatus.training_type === "segmentation" && trainingStatus.status === "running") {
  //     // CRITICAL: Only log if this is the current training run
  //     const statusRunId = trainingStatus.run_id;
  //     if (statusRunId && currentTrainingRunIdRef.current && statusRunId !== currentTrainingRunIdRef.current) {
  //       console.log(`[EXECUTE] Ignoring logs from old run ${statusRunId}, current is ${currentTrainingRunIdRef.current}`);
  //       return;
  //     }
  //     
  //     const progress = trainingStatus.progress;
  //     const message = trainingStatus.message;
  //     
  //     // Log progress updates every 5% to avoid spam
  //     if (progress !== undefined && progress > 0) {
  //       if (progress >= lastLoggedProgressRef.current + 5 || lastLoggedProgressRef.current === 0) {
  //         addLog(`Training Progress: ${progress}%`, 'info');
  //         lastLoggedProgressRef.current = progress;
  //       }
  //     }
  //     
  //     // Log training messages (loss, dice, etc.)
  //     if (message && message !== 'Training in progress...') {
  //       const now = Date.now();
  //       // Throttle message logging to once per 5 seconds
  //       if (!lastLoggedTimeRef.current || now - lastLoggedTimeRef.current > 5000) {
  //         addLog(message, 'info');
  //         lastLoggedTimeRef.current = now;
  //       }
  //     }
  //   }
  // }, [trainingStatus, currentConfig]);

  useEffect(() => {
    if (currentConfig?.app_config?.["num-server-rounds"]) {
      setTotalRounds(currentConfig.app_config["num-server-rounds"]);
    }
  }, [currentConfig]);

  // Generate feature visualizations when training completes for segmentation data
  useEffect(() => {
    const processingType = currentConfig?.dataset_config?.processing_type;
    const datasetPath = currentConfig?.dataset_config?.selected_datasets || currentConfig?.dataset_config?.dataset_path;
    const isSegmentation = processingType === "imaging_segmentation";
    
    // Check if training is completed based on status OR local completed state (which might be restored from storage)
    const trainingCompleted = isTrainingCompleted(trainingStatus, trainingProgress) || isCompleted;
    
    const hasSelectedFeatures = selectedFeatures && selectedFeatures.length > 0;
    
    const shouldGenerateVisualizations = 
      trainingCompleted &&
      hasSelectedFeatures && 
      !visualizationsGenerated; // Generate after training completion regardless of clear status

    if (shouldGenerateVisualizations) {
      setVisualizationsGenerated(true);
      setIsGeneratingVisualizations(true);
      setHasBeenCleared(false); // Reset cleared flag to allow generation
      generateFeatureVisualizations(selectedFeatures, datasetPath)
        .then((result) => {
          // addLog(`Generated visualizations for ${selectedFeatures.length} features`, 'success');
          setIsGeneratingVisualizations(false);
        })
        .catch((error) => {
          console.error('Failed to generate feature visualizations:', error);
          if (!hasBeenCleared) {
             addLog('Failed to generate feature visualizations', 'error');
          }
          setVisualizationsGenerated(false); // Reset on error
          setIsGeneratingVisualizations(false);
        });
    }
  }, [trainingStatus, trainingProgress, currentConfig, selectedFeatures, visualizationsGenerated, featureVisualizations, generateFeatureVisualizations, isTrainingCompleted, hasBeenCleared, isCompleted]);

  // Reset visualization state when training starts
  useEffect(() => {
    // Only reset if training is running AND NOT completed (progress < 100)
    // This prevents clearing visualizations when we are in the "running but finished" state
    const isFinished = isTrainingCompleted(trainingStatus, trainingProgress);
    
    if (trainingStatus?.status === 'running' && !isFinished && visualizationsGenerated) {
      setVisualizationsGenerated(false);
      setIsGeneratingVisualizations(false);
    }
  }, [trainingStatus, visualizationsGenerated, trainingProgress, isTrainingCompleted]);

  // Reset local UI state when training is not active (optimized)
  useEffect(() => {
    const status = trainingStatus?.status;
    if (status === 'idle' || status === 'error') {

      // Only reset LOCAL UI state (progress, round, clients)
      // DON'T reset isRunning - AppContext manages it based on backend status
      setProgress(prev => prev !== 0 ? 0 : prev);
      setCurrentRound(prev => prev !== 0 ? 0 : prev);
      setConnectedClients(prev => prev !== 0 ? 0 : prev);
    }
  }, [trainingStatus?.status]);

  // Track consecutive auth failures for polling
  const authFailureCountRef = useRef(0);
  const maxAuthFailures = 3; // Stop after 3 consecutive auth failures

  // Polling interval for progress updates with auth error handling
  useEffect(() => {
    let interval;
    
    // Check if training is active from multiple sources to handle page refresh/navigation
    const isTrainingActive = 
      (isRunning && !isPaused && !isCompleted) ||
      (trainingStatus?.status === 'running') ||
      (segmentationStatus === 'running' || segmentationStatus === 'in_progress');
    const isTabular = currentConfig?.dataset_config?.processing_type === 'tabular';

    if (isTrainingActive) {

      interval = setInterval(async () => {


        try {
          if (true) {
            // Always try Flower task polling first — fetchFlowerTaskStatus returns null
            // if no Flower task ID is set, in which case we fall through to the legacy path.
            const task = await fetchFlowerTaskStatus();

            if (task) {
              const { artifacts_dir, log_path, ...filteredTask } = task;
              const status = filteredTask.status;
              const progress = filteredTask.progress || {};
              const overrides = filteredTask.overrides || {};
              const taskId = filteredTask.task_id || task.task_id || 'unknown';

              const snapshotKey = JSON.stringify(filteredTask);
              if (lastFlowerStatusSnapshotRef.current !== snapshotKey) {
                const prevSnapshot = lastFlowerStatusSnapshotRef.current ? JSON.parse(lastFlowerStatusSnapshotRef.current) : null;
                lastFlowerStatusSnapshotRef.current = snapshotKey;

                // datasets_info is a structured field added by the backend when
                // selected_datasets contains more than one entry.
                const datasetsInfo = task.datasets_info || null;

                // ── User-visible multi-dataset summary (non-detailed) ──────────
                if (datasetsInfo && datasetsInfo.count > 1) {
                  addLog(
                    `Federated training across ${datasetsInfo.count} datasets: ${datasetsInfo.datasets.join(', ')}`,
                    'info',
                    false  // show by default (not behind "View Detailed Logs")
                  );
                  addLog(
                    `Simulating ${datasetsInfo.count} federated clients (one per dataset)`,
                    'info',
                    false
                  );
                }

                // ── Detailed status dump ───────────────────────────────────────
                addLog(`[FLOWER_STATUS] Raw task status for ${taskId}`, 'info', true);

                if (status) {
                  addLog(`Status: ${status}`, 'info', true);
                }

                const progressParts = [];
                if (typeof progress.current_round === 'number' && typeof progress.total_rounds === 'number') {
                  progressParts.push(`round ${progress.current_round}/${progress.total_rounds}`);
                }
                if (typeof progress.percentage === 'number') {
                  progressParts.push(`${progress.percentage}%`);
                }
                if (progressParts.length > 0) {
                  addLog(`Progress: ${progressParts.join(' · ')}`, 'info', true);
                }

                const overrideKeys = Object.keys(overrides);
                if (overrideKeys.length > 0) {
                  addLog('Overrides:', 'info', true);
                  overrideKeys.forEach((key) => {
                    if (key === 'selected_datasets' && datasetsInfo && datasetsInfo.count > 1) {
                      // Replace the raw Python list string with a readable numbered list
                      addLog(`- selected_datasets (${datasetsInfo.count} files):`, 'info', true);
                      datasetsInfo.datasets.forEach((ds, i) => {
                        addLog(`    [${i + 1}] ${ds}`, 'info', true);
                      });
                    } else {
                      addLog(`- ${key}=${overrides[key]}`, 'info', true);
                    }
                  });
                }

                // ── Success Detection (Optional summary) ──────────────────────
                if (status === 'SUCCEEDED' && prevSnapshot?.status !== 'SUCCEEDED') {
                  addLog('✅ Flower training task completed successfully.', 'success', false);
                }
              }

              // ── Failure Detection (OUTSIDE Snapshot Check) ─────────────────
              // We check this every poll to ensure we catch failures even if status is constant
              if (status === 'FAILED' || progress.stage === 'FAILED') {
                const errorMsg = filteredTask.error_message || filteredTask.error || 'The Flower task failed in the backend.';
                addLog(`❌ Training Error: ${errorMsg}`, 'error', false);
                setError(`Training Failed: ${errorMsg}`);
                setIsRunning(false);
                if (interval) clearInterval(interval);
                return task;
              }

              // ── Stuck detection — REMOVED dev-0.7.32 ──────────────────────
              // History:
              //   pre-dev-0.7.31  — auto-abort after 120s in PENDING (false-
              //                     positive on every imaging cold-start)
              //   dev-0.7.31      — bumped to 300s for imaging (false-positive
              //                     on LIDC seg, ~22 GB / site cold-cache;
              //                     CA1 finished Round-0 validation at 31s but
              //                     CA2 was still mid-validation at 5 min mark
              //                     per Backend Agent 2026-06-07 afternoon sync)
              //   dev-0.7.32 (now) — removed entirely. Backend Agent strongly
              //                     recommended Option A: trust the task-status
              //                     poll, don't self-clock. Real backend failures
              //                     are already caught by the explicit
              //                     `status === 'FAILED'` check above. Slow
              //                     cold-starts (5-15 min for LIDC seg ~22 GB
              //                     per site) are now allowed to complete; the
              //                     user can click Stop to abort manually if
              //                     they suspect something is genuinely wrong.
              //                     This removes the entire false-positive
              //                     class of "training aborted" messages on
              //                     legitimate slow imaging runs.
            }

            try {
              const rawLog = await fetchFlowerTaskLog(200);
              if (rawLog) {
                const lines = rawLog
                  .split('\n')
                  .map((l) => l.trim())
                  .filter(Boolean);
                if (lines.length > 0) {
                  const lastLine = lastFlowerLogLineRef.current;
                  let startIdx = 0;
                  if (lastLine) {
                    const idx = lines.lastIndexOf(lastLine);
                    if (idx !== -1) {
                      startIdx = idx + 1;
                    }
                  }

                  for (let i = startIdx; i < lines.length; i += 1) {
                    const line = lines[i];
                    // Surface only the most relevant Flower log lines to the user
                    const lower = line.toLowerCase();
                    const isError = lower.includes('error');
                    const isWarning = lower.includes('warn');
                    const isInfoProgress = 
                      lower.includes('[init]') || 
                      lower.includes('[run]') || 
                      lower.includes('starting flower') ||
                      lower.includes('requesting initial parameters') ||
                      lower.includes('aggregate_fit') ||
                      lower.includes('aggregate_evaluate') ||
                      lower.includes('fl_task');

                    if (isError) {
                      addLog(line, 'error', true);
                    } else if (isWarning) {
                      addLog(line, 'warning', true);
                    } else if (isInfoProgress) {
                      // Surface key progress info directly to the user
                      addLog(line, 'info', false);
                    }
                  }

                  lastFlowerLogLineRef.current = lines[lines.length - 1];
                }
              }
            } catch (logErr) {

            }

            return task;
          }

          // Imaging/segmentation and legacy tabular simulation path
          await Promise.all([
            refreshTrainingProgress().catch(err => {
              console.error('[EXECUTE] Progress fetch failed:', err.message);
              
              // Check for auth errors (401)
              if (err.message.includes('401') || err.message.includes('Unauthorized')) {
                authFailureCountRef.current += 1;
                console.warn(`[EXECUTE] Auth failure ${authFailureCountRef.current}/${maxAuthFailures}`);
                
                if (authFailureCountRef.current >= maxAuthFailures) {
                  console.error('[EXECUTE] Max auth failures reached - stopping polling');
                  addLog('⚠️ Authentication expired. Please refresh the page to continue.', 'error');
                  setError('Authentication expired. Please refresh the page and log in again.');
                  
                  // Stop training
                  setIsRunning(false);
                  if (interval) clearInterval(interval);
                }
              }
              throw err;
            }),
            refreshTrainingStatus().catch(err => {
              console.error('[EXECUTE] Status fetch failed:', err.message);
              throw err;
            })
          ]);
          
          // Reset auth failure count on success
          authFailureCountRef.current = 0;
          
          // Also fetch segmentation status if in segmentation mode
          if (currentConfig?.dataset_config?.processing_type === "imaging_segmentation") {
            await fetchCurrentSegmentationRun().catch(err => {

            });
          }
        } catch (err) {
          console.error('[EXECUTE] Polling error:', err.message);
        }
      }, 3000); // Poll every 3 seconds
    } else {

      // Reset auth failure count when training stops
      authFailureCountRef.current = 0;
    }
    
    return () => {
      if (interval) {

        clearInterval(interval);
      }
    };
  }, [isRunning, isPaused, isCompleted, trainingStatus?.status, segmentationStatus, currentConfig, refreshTrainingProgress, refreshTrainingStatus, fetchCurrentSegmentationRun, fetchFlowerTaskStatus, fetchFlowerTaskLog]);

  // Register Chart.js components
  ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);


  // Clear logs and training results but keep training status
  const handleClear = async () => {


    // Use the centralized clear function from AppContext
    await clearAllTrainingData();

    // Reset log tracking refs (local to this component)
    lastLoggedMessageRef.current = '';
    lastLoggedTimeRef.current = 0;
    trainingCompletedLoggedRef.current = false;
    lastMetricsRoundLoggedRef.current = null;
    flowerCompletionLogsLoggedRef.current = false;

    addLog('All training data and results cleared', 'info');
  };

  // Clear the saved configuration but keep session/results/logs.
  // Used by the new "Clear configuration" dropdown action.
  //
  // IMPORTANT: AppContext maintains TWO mirrored config states (`config` and
  // `currentConfig`). The Launchpad reads `config` (aliased above as
  // `currentConfig` locally) while Dashboard / handleActivityClick reads
  // `currentConfig`. We MUST clear both setters or the UI keeps showing the
  // stale config from whichever copy we missed — that was the original bug.
  const handleClearConfig = async () => {
    try {
      // Drop the persisted copies FIRST so any in-flight fetchConfig that
      // races our state updates can't repopulate from localStorage.
      try { localStorage.removeItem('savedConfig'); } catch (_) {}
      try { localStorage.removeItem('selectedFeatures'); } catch (_) {}
      try { localStorage.removeItem('persistedConfig'); } catch (_) {}

      // Best-effort: tell the backend the user has no active config so a
      // subsequent fetchConfig comes back empty. Endpoint may not exist on
      // the prototype backend, so failures are non-fatal.
      try {
        await apiPost('/config', null);
      } catch (backendError) {
        console.warn('[Execute] Backend clear-config failed (non-fatal):', backendError?.message || backendError);
      }

      // Drop BOTH in-memory config states so every reader updates immediately.
      if (typeof setConfig === 'function') {
        setConfig(null);
      }
      if (typeof setCurrentConfig === 'function') {
        setCurrentConfig(null);
      }

      // Reset feature selection state — features only make sense alongside a config.
      if (typeof setSelectedFeatures === 'function') {
        setSelectedFeatures([]);
      }
      // Reset schema validation cache: it was tied to the now-cleared selection.
      if (typeof setSchemaPreCheck === 'function') {
        setSchemaPreCheck(null);
      }
      // Reset round/progress counters that mirror config values.
      if (typeof setTotalRounds === 'function') {
        setTotalRounds(0);
      }

      addLog('Saved configuration cleared. Save a new one on the Configurations page to start training.', 'info');
    } catch (e) {
      console.warn('[Execute] handleClearConfig failed:', e?.message || e);
      addLog('Failed to clear configuration.', 'error');
    }
  };

  // Clear everything — current session data AND saved configuration.
  const handleClearAll = async () => {
    await handleClear();
    await handleClearConfig();
    addLog('Session and configuration both cleared.', 'info');
  };

  const startSegmentationPolling = async () => {
    let pollCount = 0;
    const maxPolls = 2400; // 120 minutes at 3-second intervals for very large datasets like LIDC
    let lastLoggedPhase = '';
    let lastLoggedRound = 0;
    

    
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      // Log poll count every 10 polls to show progress
      if (pollCount % 10 === 1 || pollCount <= 5) {

        addLog(`Monitoring segmentation training... (Poll ${pollCount}/${maxPolls})`, 'info');
      }
      

      
      try {
        // Refresh token every 10 minutes (200 polls) to prevent expiration during long training
        if (pollCount % 200 === 0 && auth && auth.refreshToken) {

          try {
            await auth.refreshToken();

            addLog('🔄 Token refreshed to continue monitoring', 'info');
          } catch (refreshError) {
            console.warn('[SEG_POLLING] Token refresh failed:', refreshError);
            addLog('⚠️ Token refresh failed - authentication may expire soon', 'warning');
          }
        }
        
        // Check actual backend segmentation status and update current run

        const response = await fetchCurrentSegmentationRun();

        
        if (response && response.status) {
          const status = response.status;
          const progress = response.progress || {};
          
          // CRITICAL: Check if this is the current training run
          const responseRunId = response.run_id;
          if (responseRunId && currentTrainingRunIdRef.current && responseRunId !== currentTrainingRunIdRef.current) {

            return;
          }
          
          // Update progress state and log progress percentage and epoch/round info
          if (response.percentage !== undefined) {
            // CRITICAL: Update progress state immediately for accurate progress bar display
            setProgress(response.percentage);
            
            // Also update round/epoch if available
            if (response.current_round) {
              setCurrentRound(response.current_round);
            }
            
            // Log progress
            if (response.current_epoch && response.total_epochs) {
              addLog(`Training Progress: ${response.percentage}% - Epoch ${response.current_epoch}/${response.total_epochs}`, 'info');
            } else if (response.current_round && response.num_rounds) {
              addLog(`Training Progress: ${response.percentage}% - Round ${response.current_round}/${response.num_rounds}`, 'info');
            }
          }
          
          // Show important progress messages (but not repetitive ones)
          if (progress.message && progress.message !== 'Training in progress...' && !progress.message.includes('initializing')) {
            addLog(progress.message, 'info');
          }
          
          // Show download progress if available - check response directly
          if (response.download_progress) {
            const downloadProgress = response.download_progress;
            if (downloadProgress.step_detail) {
              addLog(`${downloadProgress.step}: ${downloadProgress.step_detail}`, 'info');
            } else if (downloadProgress.percent !== undefined) {
              addLog(`Download: ${downloadProgress.percent}% - ${downloadProgress.current_step || 'Processing'}`, 'info');
            } else if (downloadProgress.formatted_progress) {
              addLog(`Download: ${downloadProgress.formatted_progress}`, 'info');
            }
          }
          
          // Process detailed logs from backend for segmentation
          if (response.detailed_logs && Array.isArray(response.detailed_logs)) {
            response.detailed_logs.forEach(detailedLog => {
              addLog(
                detailedLog.message, 
                detailedLog.level || 'debug', 
                detailedLog.detailed !== undefined ? detailedLog.detailed : true
              );
            });
          }
          
          if (status === 'completed') {
            clearInterval(pollInterval);
            setSegmentationStatus('completed');
            addLog('✅ Segmentation training completed successfully!', 'success');
            
            // Fetch final run state and visualizations after completion
            try {

              
              // First, fetch the current run state to get the summary metrics

              const finalRunState = await fetchCurrentSegmentationRun();

              
              // Then fetch visualizations

              
              if (auth && auth.refreshToken) {

                await auth.refreshToken();

              }
              

              await fetchSegmentationVisualizations('segmentation');

              addLog('✅ Segmentation results loaded successfully', 'success');
            } catch (vizError) {
              console.error('[SEG_POLLING] Failed to fetch segmentation data:', vizError);

              addLog('⚠️ Could not load segmentation results', 'warning');
            }
            return;
          } else if (status === 'failed' || status === 'error') {
            clearInterval(pollInterval);
            addLog('Segmentation training failed', 'error');
            setSegmentationStatus('failed');
            return;
          } else if (status === 'running') {
            setSegmentationStatus('running');
          }
        } else {
          // No response or no status - check if training completed

          if (pollCount > 10) { // After 30 seconds, assume completion if no active status
            clearInterval(pollInterval);
            addLog('Segmentation training completed (no active status)', 'success');
            setSegmentationStatus('completed');
            
            // Refresh token before fetching visualizations to ensure authentication
            try {
              if (auth && auth.refreshToken) {
                await auth.refreshToken();

              }
              await fetchSegmentationVisualizations('segmentation');
              addLog('✅ Segmentation results loaded successfully', 'success');
            } catch (vizError) {
              console.error('Failed to fetch segmentation visualizations:', vizError);
              addLog('⚠️ Could not load segmentation visualizations', 'warning');
            }
            return;
          }
        }
      } catch (error) {

        
        // Handle authentication errors (401) - try token refresh first, then stop polling
        if (error.message && (error.message.includes('401') || error.message.includes('UNAUTHORIZED'))) {

          
          // Try to refresh token once before giving up
          if (auth && auth.refreshToken && pollCount > 10) {
            try {
              await auth.refreshToken();
              addLog('🔄 Authentication refreshed, continuing monitoring...', 'info');
              return; // Continue polling with refreshed token
            } catch (refreshError) {
              console.warn('[EXECUTE] Token refresh failed after auth error:', refreshError);
            }
          }
          
          clearInterval(pollInterval);
          addLog('⚠️ Authentication expired during segmentation monitoring. Training may have completed.', 'warning');
          addLog('Please refresh the page to continue monitoring or check training results.', 'info');
          setSegmentationStatus('auth_expired');
          return;
        }
        
        // Handle other HTTP errors (500, 503, etc.) - stop polling after a few attempts
        if (error.message && (error.message.includes('500') || error.message.includes('503') || error.message.includes('502'))) {
          if (pollCount > 3) { // Stop after 3 server error attempts
            clearInterval(pollInterval);
            addLog('⚠️ Server error during segmentation monitoring. Training may still be running.', 'error');
            addLog('Please check backend logs or restart training if needed.', 'info');
            setSegmentationStatus('server_error');
            return;
          }
        }
        
        // If no active run (404), only consider it completed after some polls (allow time for backend to register)
        if (error.message && error.message.includes('404')) {
          if (pollCount > 5) { // Give backend 15 seconds to register the run
            clearInterval(pollInterval);
            addLog('Segmentation training completed (no active run)', 'success');
            setSegmentationStatus('completed');
            
            // Refresh token before fetching visualizations to ensure authentication
            try {
              if (auth && auth.refreshToken) {
                await auth.refreshToken();

              }
              await fetchSegmentationVisualizations('segmentation');
              addLog('✅ Segmentation results loaded successfully', 'success');
            } catch (vizError) {
              console.error('Failed to fetch segmentation visualizations:', vizError);
              addLog('⚠️ Could not load segmentation visualizations', 'warning');
            }
            return;
          } else {

          }
        }
      }
      
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
        addLog('⚠️ Segmentation polling timeout after 2 hours - backend may still be processing', 'warning');
        addLog('Large datasets like LIDC can take several hours to download and process.', 'info');
        addLog('Check backend logs or restart monitoring to continue tracking progress.', 'info');
        setSegmentationStatus('timeout');
        return;
      }
    }, 3000); // Poll every 3 seconds

    // Store interval ID for cleanup
    segmentationPollingRef.current = pollInterval;
  };

  const handleStartTraining = async () => {
    if (!currentConfig) {
      console.error('[EXECUTE] No configuration available');
      addLog('Training failed: No configuration available', 'error');
      return;
    }

    // ── Phase F1.x prototype: mock final schema validation gate ────────
    // Runs when the run is site-local (at least one completed site + capability).
    // Legacy simulation runs (dataset_path only, no sites[]) bypass the gate
    // because there are no site capabilities to validate.
    const completedSelectedSites = (selectedSites || []).filter(
      (s) => s && s.site_id && s.capability_id
    );
    // Lifted out of the if-block so the dev-0.7.6 real-submit allowlist
    // (below) can inspect it after the validation gate has run.
    let validationResult = null;
    if (completedSelectedSites.length > 0) {
      setFinalChecking(true);
      // Brief delay so the "Running…" UI state is visible (mock path). The
      // real backend path adds its own latency. runSchemaValidation selects
      // mock vs real via VITE_SCHEMA_VALIDATION_USE_MOCK (default: mock).
      await new Promise((r) => setTimeout(r, 450));
      validationResult = await runSchemaValidation({
        selectedSites,
        siteCapabilities,
        sites,
        processingType: currentConfig?.dataset_config?.processing_type,
        source: 'final',
      });
      const signature = computeSelectionSignature(
        currentConfig?.dataset_config?.processing_type,
        selectedSites
      );
      setSchemaPreCheck({ signature, result: validationResult, status: 'idle' });
      setFinalChecking(false);

      // Three-state gate (mirrors backend data review severity):
      //   fail → block training; warn → allow but surface a prompt; pass → go.
      if (validationResult.blocked) {
        addLog('Schema validation failed. Training was not started.', 'error');
        addLog(validationResult.summary, 'error');
        const c = validationResult.counts || {};
        addLog(
          `Data review: ${c.pass || 0} pass · ${c.warn || 0} warn · ${c.fail || 0} fail. See the Federated Data Review below.`,
          'error'
        );
        return;
      }
      if (validationResult.overall === 'warn') {
        const c = validationResult.counts || {};
        addLog(
          `Schema validation passed with warnings (${c.warn || 0} site${
            (c.warn || 0) === 1 ? '' : 's'
          } flagged). Proceeding with training, see the Federated Data Review below.`,
          'warning'
        );
      } else {
        addLog('Schema validation passed. Starting federated training…', 'success');
      }
    }

    // ── Phase 2 dev-0.7.6/7/12/21 real-submit allowlist ──────────────────
    // dev-0.7.6  : lifted the deferral on POST /api/execute_training_flower
    //              for tabular/primary_site_oncology_v1.
    // dev-0.7.7  : promoted BRCA Survival (tabular/brca_survival_v1) as the
    //              canonical 5-site real-data tabular demo.
    // dev-0.7.12 : remaining 4 tabular datasets (omop_tumor_cell_range,
    //              therapy_type, treatment_outcome — and the upgraded
    //              5-site primary_site_oncology pattern) lifted to
    //              production-grade. All 5 dataset_refs now accept real
    //              POST /api/execute_training_flower on any PASS pair.
    // dev-0.7.21 : imaging unblocked. Backend correction (2026-06-06):
    //              the dead /api/execute_segmentation endpoint (M-seg.A
    //              demo, unregistered in dev-0.7.21) is NOT the imaging
    //              training path. Real imaging segmentation + classification
    //              run through the SAME /api/execute_training_flower
    //              endpoint — Flask just orchestrates the flwr CLI; torch
    //              + GPU live in the ClientApp pods, not the Flask process.
    //              ProstateX-SEG Stage 1 verified end-to-end in dev-0.7.21
    //              (12/12 PASS, backend commit 180c358). To unblock the
    //              real path on FE side, we (a) accept imaging processing
    //              types in the gate below and (b) add the 4 imaging
    //              dataset_refs to the allowlist set.
    //
    // 6-item allowlist:
    //   1. env opted in:      VITE_TRAINING_USE_MOCK === "false"
    //   2. site-local mode:   at least one completed (site_id + capability_id)
    //   3. <= 2 sites:        dev_execution_mode.max_logical_sites_per_run
    //   4. allowed pipeline:  processing_type in REAL_SUBMIT_PROCESSING_TYPES
    //                         (tabular + imaging_segmentation + imaging_classification)
    //   5. demo path only:    every cap's dataset_ref is in ALLOWED_DEMO_DATASET_REFS
    //   6. data readable:     no site_result emits DATA_FILE_NOT_READABLE
    // All 6 true → call AppContext.executeTraining() which posts to
    // /api/execute_training_flower. ANY check failing → falls through to
    // the mock playback path further down in this function (see
    // playMockTraining call ~line 1519). Mock playback is intentionally
    // PRESERVED — see the "Restoring mock for quick demo" note below.
    //
    // ── Restoring mock playback for an imaging dataset (quick-demo) ──
    // The mock playback path is gated only by `realSubmitAllowed === false`.
    // To send an imaging dataset BACK to mock playback (e.g. for an offline
    // demo where AKS DEV is unreachable, or to demo the legacy fake-stream
    // UX), pick whichever revert is easiest:
    //   Option A — env override (no code change, no redeploy needed if you
    //              already serve a build with VITE_TRAINING_USE_MOCK=true):
    //              this disables real-submit for ALL datasets. Coarse.
    //   Option B — comment out the relevant entries in
    //              ALLOWED_DEMO_DATASET_REFS below. Targeted: keeps real
    //              submit for the rest. THIS IS THE EXPECTED REVERT PATH
    //              for "I just want imaging seg to go back to mock for one
    //              demo." Re-add the lines + rebuild + redeploy to undo.
    //   Option C — remove imaging from REAL_SUBMIT_PROCESSING_TYPES below.
    //              Coarser than B (drops all imaging at once).
    // None of these touch the mock playback code itself, so the fake-stream
    // demo UX remains intact and reachable at any time.
    const REAL_SUBMIT_PROCESSING_TYPES = new Set([
      'tabular',
      // dev-0.7.21: imaging routes through /execute_training_flower → flwr
      // CLI → SuperLink → ClientApp pod. Flask process does not import
      // torch — that lives in the ClientApp container image.
      'imaging_segmentation',
      'imaging_classification',
    ]);
    const ALLOWED_DEMO_DATASET_REFS = new Set([
      // ── Tabular ──────────────────────────────────────────────────────
      'tabular/primary_site_oncology_v1',  // dev-0.7.3 → dev-0.7.12 (5-site, diversified scenarios)
      'tabular/brca_survival_v1',          // dev-0.7.7 canonical BRCA 5-site real-data demo
      'tabular/omop_tumor_cell_range_v1',  // dev-0.7.12 (3-site: TGH/SUN pass, SMH warn)
      'tabular/therapy_type_v1',           // dev-0.7.12 (4-site: TGH/SUN pass, MTL warn, BCCRC fail)
      'tabular/treatment_outcome_v1',      // dev-0.7.12 (3-site: TGH/OICR pass, SMH fail — no SUN)
      // ── Imaging ─────────────────────────────────────────────────────
      // dev-0.7.21: confirmed routable via /api/execute_training_flower.
      // To send any one of these back to mock playback for a quick demo,
      // comment out its line here and rebuild — the rest of the allowlist
      // (including tabular) keeps working.
      'prostatex_seg_v1',                  // imaging segmentation (ProstateX-SEG Stage 1 verified dev-0.7.21)
      'prostatex_cls_v1',                  // imaging classification
      'lidc_seg_v1',                       // imaging segmentation
      'lidc_cls_v1',                       // imaging classification
    ]);
    const _processingTypeForAllowlist = currentConfig?.dataset_config?.processing_type;
    const _allCapsInDemoAllowlist =
      completedSelectedSites.length > 0 &&
      completedSelectedSites.every((s) => {
        const caps = siteCapabilities?.[s.site_id] || [];
        const cap = caps.find((c) => c.capability_id === s.capability_id);
        return cap?.dataset_ref && ALLOWED_DEMO_DATASET_REFS.has(cap.dataset_ref);
      });
    const _hasDataFileNotReadable = (validationResult?.siteResults || []).some(
      (r) => (r.findings || []).some((f) => f.code === 'DATA_FILE_NOT_READABLE')
    );
    const _isTrainingMockMode =
      String(import.meta.env.VITE_TRAINING_USE_MOCK ?? 'true') !== 'false';
    const realSubmitAllowed =
      !_isTrainingMockMode &&
      completedSelectedSites.length > 0 &&
      completedSelectedSites.length <= 2 &&
      REAL_SUBMIT_PROCESSING_TYPES.has(_processingTypeForAllowlist) &&
      _allCapsInDemoAllowlist &&
      !_hasDataFileNotReadable;

    if (realSubmitAllowed) {
      addLog(
        `Submitting real Flower training to AKS DEV (${_processingTypeForAllowlist} pipeline).`,
        'info'
      );
      try {
        const response = await executeTraining();
        if (response?.task_id) {
          addLog(`Training task started, task_id: ${response.task_id}`, 'success');
        }
        if (response?.overrides?.expected_schema_id) {
          addLog(
            `Expected schema: ${response.overrides.expected_schema_id}`,
            'info'
          );
        }
        if (typeof response?.overrides?.selected_datasets === 'string') {
          addLog(
            `Selected datasets (redacted): ${response.overrides.selected_datasets}`,
            'info',
            true
          );
        }
        // executeTraining() sets isRunning=true and captures task_id into
        // flowerTaskIdRef. The polling useEffect takes over — skip the
        // mock playback path entirely.
        return;
      } catch (err) {
        console.error('[execute] real training submit failed:', err);
        addLog(
          `Real training submit failed: ${err?.message || err}. Falling back to mock playback.`,
          'warning'
        );
        // fall through to the mock playback path so the user still sees
        // progress in the UI (won't trigger a real Flower run).
      }
    }

    try {
      // ── Prototype mock playback path (no real backend stream) ─────────
      // Reset the surface, then drive Execution Logs / progress / round /
      // clients from a server-side-only mock stream selected by pipeline.
      // Source: 2026-05-25_training_progress_mock_server_side_only_v2.md.
      // Explicitly no ClientApp pod stdout, no per-epoch losses, no GPU
      // banner — those are out of scope for this round.

      // 1. Cancel any in-flight playback from a previous Start click.
      if (mockPlaybackRef.current) {
        mockPlaybackRef.current.cancel();
        mockPlaybackRef.current = null;
      }

      // 2. Clear any cached side state so the new run doesn't bleed into
      //    the old. clearCachedData is a no-op against the backend in
      //    prototype mode; we still call it to keep local reset consistent.
      await clearCachedData();
      localStorage.removeItem('floatingProgressClosed');
      localStorage.removeItem('floatingProgressUserClosed');
      trainingCompletedLoggedRef.current = false;
      try { localStorage.removeItem('executeCompletionLogged'); } catch (_) {}

      // 3. Clear logs so old playback lines don't mix with the new one.
      setLogs([]);
      localStorage.removeItem('executeLogs');
      lastLoggedMessageRef.current = '';
      lastLoggedTimeRef.current = 0;
      lastLoggedProgressRef.current = 0;
      currentTrainingRunIdRef.current = null;
      lastFlowerLogLineRef.current = null;

      // 4. Reset progress / round / completion state.
      setIsCompleted(false);
      setIsRunning(true);
      setProgress(0);
      setCurrentRound(0);
      setConnectedClients(0);
      setTrainingResults(null);
      setHasBeenCleared(false);

      // 5. Select the pipeline template. processing_type values seen here:
      //    "tabular" / "imaging_classification" / "imaging_segmentation".
      //    Legacy single value "segmentation" is mapped to imaging_segmentation
      //    via the helper. num_rounds comes from the user's config.
      const processingType = currentConfig?.dataset_config?.processing_type;
      const imagingTask = currentConfig?.imaging_config?.imaging_task;
      const numRounds = Number(
        currentConfig?.app_config?.["num-server-rounds"]
      ) || undefined;
      const template = selectLogTemplate({
        processingType,
        imagingTask,
        numRounds,
      });
      setTotalRounds(template.numRounds);

      // 6. Kick off the streaming playback. Cancellation handle is stashed
      //    on the ref so Restart and unmount can both stop it.
      //
      //    Two completion signals on this surface:
      //      - onAllRoundsDone: fires the moment the last round's
      //        aggregate_evaluate ticks. We flip status to Completed HERE so
      //        the UI doesn't sit at "in progress" while Tier1 / SUMMARY
      //        lines continue to scroll into the log panel.
      //      - onComplete: fires at the very end of the stream (after the
      //        UI-synthesized completion footer). At that point everything
      //        is already in the right state; we just clear the ref.
      mockPlaybackRef.current = playMockTraining({
        template,
        addLog,
        onProgress: (p) => setProgress(p),
        onRoundChange: (round) => setCurrentRound(round),
        onClientsChange: (clients) => setConnectedClients(clients),
        onAllRoundsDone: () => {
          setIsRunning(false);
          setIsCompleted(true);
          setProgress(100);
        },
        onComplete: () => {
          mockPlaybackRef.current = null;
          // Defensive — these are already set by onAllRoundsDone for any
          // template that contains rounds. Setting them again is idempotent
          // and protects the generic-fallback template that has no round.
          setIsRunning(false);
          setIsCompleted(true);
          setProgress(100);
        },
      });
    } catch (error) {
      console.error('[EXECUTE] Mock playback failed to start:', error);
      addLog(`Training failed: ${error.message || error}`, 'error');
      setIsRunning(false);
    }
  };

  const handlePause = () => {
    // For segmentation, we don't have pause functionality yet
    if (currentConfig?.dataset_config?.processing_type === "imaging_segmentation") {

      addLog('⚠️ Pause not supported for segmentation training', 'warning');
      return;
    }
    
    addLog('⏸️ Pausing training...', 'info');
    pauseTraining()
      .then(() => {
        addLog('⏸️ Training paused successfully', 'success');
      })
      .catch((error) => {
        console.error('[EXECUTE] Pause failed:', error);
        addLog(`Failed to pause training: ${error.message || error}`, 'error');
      });
  };

  const handleStop = async () => {
    // Cancel any in-flight mock playback so the timer doesn't keep
    // dripping log lines after the user clicks Stop. This is a no-op when
    // no playback is running.
    if (mockPlaybackRef.current) {
      mockPlaybackRef.current.cancel();
      mockPlaybackRef.current = null;
      addLog('Mock training playback stopped.', 'warning');
      setIsRunning(false);
    }

    // Check if this is segmentation mode
    if (currentConfig?.dataset_config?.processing_type === "imaging_segmentation") {

      addLog('Stopping segmentation training...', 'info');
      
      try {
        const response = await apiPost('/seg/stop', { force: true });

        
        // Clear polling interval
        if (segmentationPollingRef.current) {
          clearInterval(segmentationPollingRef.current);
          segmentationPollingRef.current = null;
        }
        
        // Update status
        setSegmentationStatus('stopped');
        
        // Reset local progress state to hide progress bar
        setProgress(0);
        setCurrentRound(0);
        setConnectedClients(0);
        
        addLog('Segmentation training stopped successfully', 'success');
      } catch (error) {
        console.error('[EXECUTE] Segmentation stop failed:', error);
        addLog(`Failed to stop segmentation training: ${error.message || error}`, 'error');
      }
      return;
    }

    addLog('Stopping training...', 'info');
    stopTraining()
      .then(() => {
        // Reset local progress state to hide progress bar
        setProgress(0);
        setCurrentRound(0);
        setConnectedClients(0);
        
        addLog('Training stopped successfully', 'success');
      })
      .catch((error) => {
        console.error('[EXECUTE] Stop failed:', error);
        addLog(`Failed to stop training: ${error.message || error}`, 'error');
      });
  };

  // Add a counter to ensure unique log IDs
  const logCounterRef = useRef(0);
  const segmentationPollingRef = useRef(null);
  const lastLoggedMessageRef = useRef('');
  const lastLoggedTimeRef = useRef(0);
  const trainingCompletedLoggedRef = useRef(false);
  const lastMetricsRoundLoggedRef = useRef(null);
  const flowerCompletionLogsLoggedRef = useRef(false);
  const prevStatusRef = useRef('idle');
  const currentTrainingRunIdRef = useRef(null);  // Track current training run_id
  const lastFlowerLogLineRef = useRef(null);     // Track last processed Flower CLI log line
  const lastFlowerStatusSnapshotRef = useRef(null);

  const addLog = (message, level = "info", detailed = false) => {
    const now = Date.now();
    const timeDiff = now - lastLoggedTimeRef.current;
    
    // Prevent duplicate logs within 1 second with same message
    // BUT allow progress updates (they contain changing percentages/rounds)
    const isProgressUpdate = message.includes('Progress:') || message.includes('Round ') || message.includes('progress:');
    if (lastLoggedMessageRef.current === message && timeDiff < 1000 && !isProgressUpdate) {
      return;
    }
    
    lastLoggedMessageRef.current = message;
    lastLoggedTimeRef.current = now;
    
    const timestamp = getCurrentUTCDate();
    logCounterRef.current += 1;
    const newLog = {
      id: `${Date.now()}-${logCounterRef.current}`, // Ensure unique ID
      timestamp,
      message,
      level,
      detailed // Mark if this is a detailed log
    };
    
    setLogs(prevLogs => {
      // Check if message matches any of the last 3 logs to prevent duplicates
      // This protects against component remounts causing re-logging of "completion" events
      const lastLogs = prevLogs.slice(-3);
      const isDuplicate = lastLogs.some(log => log.message === message);
      
      if (isDuplicate) {
        return prevLogs;
      }

      // Clear logs if they contain invalid dates (one-time check)
      if (prevLogs.length > 0 && prevLogs[0].timestamp && !prevLogs[0].timestamp.includes('T')) {
          return [newLog];
      }
      
      const updatedLogs = [newLog, ...prevLogs].slice(0, 1000);
      try {
        localStorage.setItem('executeLogs', JSON.stringify(updatedLogs));
      } catch (e) {
        console.error("Failed to save logs to storage", e);
      }
      return updatedLogs;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return colors.greenAccent?.[500] || '#4caf50';
      case 'paused': return colors.redAccent?.[300] || '#e57373';
      case 'completed': return colors.blueAccent?.[600] || '#1e88e5';
      default: return colors.grey?.[400] || '#999999';
    }
  };

  const getStatusText = (status) => {
    if (!status || status === 'idle') return t('execute.readyToStart');
    switch (status) {
      case 'running': return t('execute.running');
      case 'paused': return t('execute.paused');
      case 'completed': return t('execute.completed');
      default: return t('execute.readyToStart');
    }
  };

  // Load selected config from URL parameters on component mount
  useEffect(() => {
    const loadSelectedConfig = () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const configParam = queryParams.get('config');
        
        if (configParam) {
          const parsedConfig = JSON.parse(decodeURIComponent(configParam));
          
          // Update the config in the context without saving to backend
          if (parsedConfig) {
            // Remove the config parameter from the URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            
            // Update the config in the context
            setCurrentConfig(parsedConfig);
          }
        }
      } catch (error) {
        console.error('Error loading selected config:', error);
      }
    };

    loadSelectedConfig();
  }, []); // Empty dependency array means this runs once on mount

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (segmentationPollingRef.current) {
        clearInterval(segmentationPollingRef.current);
      }
    };
  }, []);

  // Show loading state while config is being fetched, but avoid flicker if we already have a config
  if (configLoading && !currentConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <Typography variant="body1" sx={{ color: colors?.grey?.[300] || '#cccccc' }}>
          Loading configuration...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: 'auto' }}>
      <HeaderWithControls
        icon={<RocketLaunchIcon />}
        title={t('execute.title')}
        subtitle={t('execute.description')}
        customControls={
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<HelpOutlineIcon />}
            onClick={() => setHelpOpen(true)}
                        sx={{ color: colors.grey[500], borderColor: colors.grey[800] }}

          >
            Help
          </Button>
        }
      />

      <HelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Execution Help"
        items={[
          {
            title: "Training Status",
            icon: <AutorenewIcon sx={{ fontSize: 20 }}/>,
            description: "Monitor the progress of your federated learning task. View rounds, accuracy metrics, and logs in real-time."
          },
          {
            title: "Controls",
            icon: <PlayCircleFilledIcon sx={{ fontSize: 20 }}/>,
            description: (
               <span>
                <strong>Start:</strong> Initiate the training process across available clients.<br/>
                <strong>Stop:</strong> Terminate the current training session.<br/>
                <strong>Clear Logs:</strong> Remove execution history from the view.
              </span>
            )
          },
          {
            title: "Results",
            icon: <AssessmentIcon sx={{ fontSize: 20 }}/>,
            description: "After training completes, view detailed charts for accuracy and loss over time, as well as feature importance analysis if applicable."
          }
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Left Column - Training Status + Pre-run Checks (1/3 width on desktop).
            The whole column is wrapped in a single sticky container so all
            three panels (status KPIs, configuration details, pre-run checks)
            scroll together and stay pinned while the user reads the longer
            right column (controls / animation / results / logs). */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, alignSelf: 'flex-start' }}>
            <TrainingStatusCard
              colors={colors}
              currentConfig={currentConfig}
              trainingStatus={trainingStatus}
              segmentationStatus={segmentationStatus}
              segmentationProgress={segmentationProgress}
              isCompleted={isCompleted}
              hasBeenCleared={hasBeenCleared}
              progress={progress}
              currentRound={currentRound}
              totalRounds={totalRounds}
              selectedFeatures={selectedFeatures}
              featureMetadata={featureMetadata}
              getStatusColor={getStatusColor}
              getStatusText={getStatusText}
              isRunning={isRunning}
              perDatasetFeaturesMap={perDatasetFeaturesMap}
              selectedSites={selectedSites}
              siteCapabilities={siteCapabilities}
              availableDatasets={availableDatasets}
            />

            {/* Pre-run Checks — sits directly under Configuration Details. */}
            <PreRunChecksPanel currentConfig={currentConfig} isFinalChecking={finalChecking} />
          </Box>
        </Grid>

        {/* Right Column - Controls, Results, Logs (2/3 width on desktop) */}
        <Grid item xs={12} lg={8}>
          {/* Launch status banner — anchors the page with a clear state cue */}
          {(() => {
            let bannerStatus = 'idle';
            if (isRunning && !isCompleted) bannerStatus = 'running';
            else if (isPaused) bannerStatus = 'paused';
            else if (isCompleted) bannerStatus = 'completed';
            return (
              <LaunchStatusBanner
                status={bannerStatus}
                currentConfig={currentConfig}
                progress={progress}
              />
            );
          })()}

          {/* dev-0.7.11 — surface the Federated Data Review for ALL three
              outcomes (pass / warn / fail). Previously only warn + fail
              showed the card; user feedback (2026-06-04) wanted the
              full per-site breakdown visible on /execute even when both
              sites are clean PASS, mirroring the level of detail shown
              on /configs. The card itself handles the pass case with a
              green header + per-row PASS chip + no findings rendered. */}
          {schemaPreCheck?.result &&
            (schemaPreCheck.result.overall === 'pass' ||
              schemaPreCheck.result.overall === 'warn' ||
              schemaPreCheck.result.overall === 'fail') && (
              <SchemaValidationDetailsCard
                result={schemaPreCheck.result}
                retrying={finalChecking}
                onReRun={async () => {
                  await handleStartTraining();
                }}
              />
            )}

          <TrainingControlPanel
            colors={colors}
            currentConfig={currentConfig}
            segmentationStatus={segmentationStatus}
            segmentationLoading={segmentationLoading}
            configLoading={configLoading}
            isRunning={isRunning}
            isPaused={isPaused}
            isCompleted={isCompleted}
            logs={logs}
            isClearing={isClearing}
            handleStartTraining={handleStartTraining}
            handleStop={handleStop}
            handleClear={handleClear}
            handleClearConfig={handleClearConfig}
            handleClearAll={handleClearAll}
            onShowAllExecutions={() => {
              setSelectedRunId(null);
              setShowAllCompletedRuns(true);
            }}
          />
          
          <TrainingAnimation
            isRunning={isRunning}
            isPaused={isPaused}
            isCompleted={isCompleted}
            progress={progress}
            currentRound={currentRound}
            totalRounds={totalRounds}
            connectedClients={connectedClients}
            hasError={Boolean(error)}
          />

          {/* Segmentation Visualizations */}
          {currentConfig?.dataset_config?.processing_type === "imaging_segmentation" && segmentationStatus === 'completed' && segmentationVisualizations && segmentationVisualizations.length > 0 && (() => {

            return (
              <SegmentationResults
                colors={colors}
                segmentationVisualizations={segmentationVisualizations}
                segmentationCurrentRun={currentSegmentationRun}
              />
            );
          })()}

          {/* Training Results Chart with integrated Feature Distribution
              Analysis — hidden per dev-0.7.7 user feedback (2026-06-03).
              Pipeline Result Card below is now the canonical post-training
              surface. Block kept as commented-out reference in case any
              part is needed later; the import is unused but harmless.
              {(isCompleted && trainingResults && Object.keys(trainingResults).length > 0) && (
                <IntegratedTrainingResults
                  colors={colors}
                  trainingResults={trainingResults || {}}
                  currentConfig={currentConfig}
                  selectedFeatures={selectedFeatures}
                  featureVisualizations={featureVisualizations}
                  isGeneratingVisualizations={isGeneratingVisualizations}
                  t={t}
                />
              )}
          */}

          {/* Pipeline result card (prototype-only static HTML artifact) — */}
          {/* renders after the mock run completes. Card URL resolves from   */}
          {/* the user's processing_type + selected site capabilities.       */}
          {isCompleted && (() => {
            const cardMeta = resolveMockResultCard({
              processingType: currentConfig?.dataset_config?.processing_type,
              imagingTask: currentConfig?.imaging_config?.imaging_task,
              selectedSites,
              siteCapabilities,
            });
            // Phase F2 dev-0.7.7 — pass the live task_id (when present)
            // so the viewer can attempt to swap the mock iframe URL for
            // the real Tier 1 result card via
            //   GET /api/flower_tasks/{task_id}/tier1/Tier1_result_card.html
            // 404 / not-yet-ready / mock baseline → viewer falls back to
            // cardMeta.url. Source of truth for the live task_id is
            // trainingStatus.flower_task.task_id (set by AppContext's
            // fetchFlowerTaskStatus after a real /execute_training_flower).
            const liveTaskId = trainingStatus?.flower_task?.task_id || null;
            // dev-0.7.8 — tier1_summary is inlined in
            // GET /api/flower_tasks/{task_id} (T3 fast-path). Pull it
            // out of trainingStatus.flower_task (which is the raw task
            // body captured by AppContext.fetchFlowerTaskStatus) so the
            // viewer can render real ROC-AUC / PR-AUC / accuracy /
            // confusion matrix etc. instead of mock summary.
            const liveTier1Summary =
              trainingStatus?.flower_task?.tier1_summary || null;
            // Resolve a user-facing dataset name to feed into the
            // PipelineResultCardViewer summary heading (otherwise it
            // renders "n/a"). Resolution order:
            //   1. Live form state `selectedSites` (still populated when
            //      user is on /execute after just configuring at /configs).
            //   2. Persisted `currentConfig.sites` (dev-0.7.50 onward —
            //      survives refresh / navigate-away).
            //   3. Same model+processing_type reverse-inference path the
            //      Execution History list uses, so even when sites[] is
            //      empty (legacy / mid-failure) we get a category label
            //      like "ProstateX Classification" instead of "n/a".
            const liveDatasetName = (() => {
              const completedSelected = (selectedSites || []).filter(
                (s) => s && s.site_id && s.capability_id
              );
              const savedSites = Array.isArray(currentConfig?.sites)
                ? currentConfig.sites
                : [];
              const synthRow = {
                config_data: {
                  sites: completedSelected.length > 0 ? completedSelected : savedSites,
                },
                processing_type: currentConfig?.dataset_config?.processing_type,
                modelType: currentConfig?.dataset_config?.model_type,
              };
              const resolved = resolveDatasetDisplayNameForExecution(synthRow, {
                siteCapabilities,
                availableDatasets,
              });
              // Treat sentinel "Unknown" / "Unknown Dataset" as null so
              // tier1Artifacts can still try meta.dataset_name; otherwise
              // we'd lock in "Unknown" even when backend has a real name.
              if (!resolved || /^Unknown( Dataset)?$/i.test(resolved)) return null;
              return resolved;
            })();
            return cardMeta?.url ? (
              <PipelineResultCardViewer
                cardUrl={cardMeta.url}
                cardLabel={cardMeta.label}
                pipelineKey={cardMeta.key}
                taskId={liveTaskId}
                tier1Summary={liveTier1Summary}
                datasetDisplayName={liveDatasetName}
              />
            ) : null;
          })()}

          {/* Logs Card */}
          <ExecutionLogsCard
            colors={colors}
            logs={[...logs].reverse()}
          />
        </Grid>
      </Grid>
      <AllCompletedRunsModal
        open={showAllCompletedRuns}
        onClose={() => setShowAllCompletedRuns(false)}
        activities={getAllCompletedRuns}
        onActivityClick={handleActivityClick}
        initialSelectedRunId={selectedRunId}
      />
    </Box>
  );
};

export default Execute;
